import { queryOptions, useQuery } from '@tanstack/react-query';
import { ApiError } from '../../components/ApiLoadingGuard/ApiLoadingGuard';
import {
  JobsiteService,
  OrganisationService,
  UserControllerGetUsersResponse,
  UserPopulatedDTO,
  UserService,
} from '../generated';

export const userKeys = {
  base: ['user'],
  read: ({ authId }: UserOptionsProps) => [...userKeys.base, authId],
  list: ({ organisationId }: UserListOptionsProps) => [...userKeys.base, organisationId, 'list'] as const,
};

export interface UserOptionsProps {
  authId: string | undefined;
}

export interface UserListOptionsProps {
  organisationId: string | undefined;
}

export function userOptions({ authId }: UserOptionsProps) {
  return queryOptions<UserPopulatedDTO>({
    queryKey: userKeys.read({ authId }),
    queryFn: async () => {
      if (!authId) throw new Error('authId is required');

      // user
      const userResponse = await UserService.userControllerGetUser({ path: { authId: authId } });
      if (!userResponse.data) {
        throw (userResponse as any).error || new Error(userResponse.response?.statusText || 'Unknown error');
      }

      // organisation
      const organisationResponse = await OrganisationService.organisationControllerGetOrganisation({
        path: { organisationId: userResponse.data.organisationId },
      });
      if (!organisationResponse.data) {
        throw (
          (organisationResponse as any).error || new Error(organisationResponse.response?.statusText || 'Unknown error')
        );
      }

      // jobsites
      const jobsiteResponses = await Promise.all(
        userResponse.data.jobsiteIds.map((jobsiteId) =>
          JobsiteService.jobsiteControllerGetJobsite({
            path: { organisationId: organisationResponse.data.id, jobsiteId: jobsiteId },
          })
        )
      );
      if (jobsiteResponses.some((res) => !res.data)) {
        throw new Error('Failed to fetch one or more jobsites');
      }
      const jobsites = jobsiteResponses.map((res) => {
        return res.data!;
      });
      const preferredJobsite = jobsites.find((j) => j.id === userResponse.data.preferredJobsiteId);

      const result: UserPopulatedDTO = {
        ...userResponse.data,
        organisation: organisationResponse.data,
        jobsites: jobsites,
        preferredJobsite: preferredJobsite,
      };

      return result;
    },
    enabled: !!authId,
  });
}

export function userListOptions({ organisationId }: UserListOptionsProps) {
  return queryOptions<UserControllerGetUsersResponse, ApiError>({
    queryKey: userKeys.list({ organisationId }),
    queryFn: async () => {
      if (!organisationId) {
        throw new Error('organisationId is required');
      }

      const response = await UserService.userControllerGetUsers({
        query: {
          filter: [
            {
              organisation: { in: [organisationId] },
            },
          ],
        },
      });

      if (response.data !== undefined) {
        return response.data;
      } else {
        throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
      }
    },
    enabled: !!organisationId,
  });
}

export function useUser(props: UserOptionsProps) {
  return useQuery(userOptions(props));
}

export function useUserList(props: UserListOptionsProps) {
  return useQuery(userListOptions(props));
}
